// Import package components
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Grid, Image } from "uikit-react";
import { AutoComplete, Input, Select } from "antd";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import clsx from "clsx";
import * as yup from "yup";
import debounce from "lodash.debounce";
import moment from "moment";
import UIkit from "uikit";
import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { AppContexts } from "../../../providers";
import Tags from "../../common/Tags";
import ImagePreview from "../../common/ImagePreview";
import ConvertUTCDateToLocalDate from "../../common/ConvertUTCDateToLocalDate";
import { WORK_ORDER_CATEGORY_OPTIONS } from "../categoryOptions";
import { TASK_TYPE_OPTIONS } from "../taskTypeOptions";
import { PRIORITY_OPTIONS, getPriorityLabel } from "../priorityOptions";
import PropertyDetailsModal from "../../common/PropertyDetailsModal";
import { useDefaultSensors } from "../../common/SortUtils";
import ChecklistItemModal, { ITEM_TYPES } from "../../common/ChecklistItem";
import SortableChecklistItem from "../../common/SortableChecklistItem";
import AssigneePickerModal from "../AssigneePickerModal";
import TaskRecurrenceFields, { buildRulePayload } from "../TaskRecurrenceFields";
import {
  buildFutureRuleUpdatePayload,
  dateInputValue,
  recurrenceSeedFromScheduledDate,
  recurrenceValueFromRule,
} from "./recurrenceHelpers";
import { WorkSchedulesContext } from "../../../providers/work_schedules.provider";
import { buildScheduleFromDateAvail } from "../../../utils/availabilityUtils";
import WorkOrderService from "../../../services/workorder.service";
import TaskRecurrenceRuleService from "../../../services/taskRecurrenceRule.service";
import TaskOccurrencesService from "../../../services/taskOccurrences.service";
import {
  isRecurringWorkOrder,
  promptRecurringEditScope,
} from "../recurringScopePrompt";
import { clearStaleUIKitModalOverlays } from "../workOrderAddPanel";

const buildSchema = (requireTaskType, requireRental) => yup.object().shape({
  summary: yup.string().required(),
  task_type: requireTaskType
    ? yup.string().nullable().required("Task type is required")
    : yup.string().nullable(),

  // Manager
  manager_id: yup.string().nullable(),

  rental_id: requireRental
    ? yup.string().nullable().required("Property is required")
    : yup.string().nullable(),
});

const normalizeItems = (maybe, genUUID) => {
  if (!Array.isArray(maybe)) return [];
  return maybe.map((it) => ({
    id: it.id,
    _uiId: genUUID(), // for React keys
    label: it.label || "",
    item_type: it.item_type || "yes_no",
    config: it.config || null,
    description: it.description || "",
    is_required: it.is_required ?? true,
    require_photo: it.require_photo ?? false,
    require_note: it.require_note ?? false,
  }));
};

const closeWorkOrderAdd = () => {
  const el = document.getElementById("work-order-add");
  if (!el) return;
  UIkit.offcanvas(el).hide();
};

const getOccurrenceStartDate = (workOrder, existingSchedule, assignScheduleStartDate) =>
  assignScheduleStartDate ||
  dateInputValue(existingSchedule?.scheduled_date) ||
  dateInputValue(workOrder?.scheduled_date) ||
  null;

const WorkOrderAdd = (props) => {
  const {
    grId,
    msgId,
    msg_gr,
    itemLabel = "Work Order",
    taskTypeLabel = "Task Type*",
    showTaskType = false,
    showRecurrence = false,
    showDelete = true,
    onSaved,
  } = props;

  const workOrderContext = useContext(AppContexts.WorkOrderContext);
  const rentalContext = useContext(AppContexts.RentalsContext);
  const reservationContext = useContext(AppContexts.ReservationsContext);
  const checklistsContext = useContext(AppContexts.ChecklistsContext);
  const utilContext = useContext(AppContexts.UtilContext);
  const { user } = useContext(AppContexts.AuthContext);
  const workSchedulesCtx = useContext(WorkSchedulesContext);
  const sensors = useDefaultSensors();

  const { isNew, workOrder, insertWorkOrder, updateWorkOrder, isProcessing,
    getSchedule: fetchWorkOrderSchedule,
    createSchedule: createWorkOrderSchedule,
    updateSchedule: updateWorkOrderSchedule,
    recurringEditScope,
    setRecurringEditScope,
  } = workOrderContext;

  const [guestKey, setGuestKey] = useState(null);
  const [propertyKey, setPropertyKey] = useState(null);

  const [property, setProperty] = useState(null);
  const [guest, setGuest] = useState(null);

  const [assignList, setAssigns] = useState([]);
  const [attachmentList, setAttachments] = useState([]);
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [selectedChecklistTemplate, setSelectedChecklistTemplate] = useState("");
  const [checklistPage, setChecklistPage] = useState(0);
  const [checklistHasMore, setChecklistHasMore] = useState(true);
  const [loadingChecklists, setLoadingChecklists] = useState(false);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  // Recurrence is owned by TaskRecurrenceFields and updated through onChange.
  // When recurrence.enabled === true the form submits to the
  // /task-recurrence-rules endpoint instead of /workorders.
  const [recurrence, setRecurrence] = useState({ enabled: false });
  const [checklistTemplateTouched, setChecklistTemplateTouched] = useState(false);
  const [formOpenVersion, setFormOpenVersion] = useState(0);
  const clearFormRef = useRef(() => {});

  const formSchema = useMemo(
    // Property required for new tasks only; standalone WOs stay optional until
    // the new work-order architecture ships — then drop the && showTaskType guard.
    () => buildSchema(showTaskType, isNew && showTaskType),
    [showTaskType, isNew]
  );

  const [checkOpen, setCheckOpen] = useState(false);
  const [checkMode, setCheckMode] = useState("add"); // "add" | "edit"
  const [checkInitial, setCheckInitial] = useState(null);
  const [propertyDetailsOpen, setPropertyDetailsOpen] = useState(false);

  const [assignPickerOpen, setAssignPickerOpen] = useState(false);
  const [assignDraft, setAssignDraft] = useState([]); // local selection inside modal

  const [assignPickerDayOffset, setAssignPickerDayOffset] = useState(0); // 0=today
  const [focusedAssignee, setFocusedAssignee] = useState(null); // for right-side details

  const [assignScheduleStartDate, setAssignScheduleStartDate] = useState("");
  const [assignScheduleStartTime, setAssignScheduleStartTime] = useState("");
  const [assignScheduleDuration, setAssignScheduleDuration] = useState("60"); // minutes
  const [existingSchedule, setExistingSchedule] = useState(null); // fetched from API
  const [loadingExistingSchedule, setLoadingExistingSchedule] = useState(false);

  const [availability, setAvailability] = useState([]);

  // Ref to hold the latest context so fetchAvailability stays stable
  const workSchedulesCtxRef = useRef(workSchedulesCtx);
  useEffect(() => { workSchedulesCtxRef.current = workSchedulesCtx; });

  useEffect(() => {
    const el = document.getElementById("work-order-add");
    if (!el) return undefined;
    const onShown = () => {
      clearStaleUIKitModalOverlays();
      setFormOpenVersion((prev) => prev + 1);
    };
    const onHidden = () => {
      initializedOpenVersionRef.current = 0;
      setRecurringEditScope?.(null);
      clearFormRef.current?.();
    };
    UIkit.util.on(el, "shown", onShown);
    UIkit.util.on(el, "hidden", onHidden);
    return () => {
      UIkit.util.off(el, "shown", onShown);
      UIkit.util.off(el, "hidden", onHidden);
    };
  }, [setRecurringEditScope]);
  const fetchingAvailabilityRef = useRef(false);
  const availCacheRangeRef = useRef({ start: null, end: null }); // ISO date range already fetched

  const fetchChecklistTemplates = useCallback(async (page = 0, reset = false) => {
    if (!user) return;
    
    setLoadingChecklists(prev => {
      if (prev) return prev;
      return true;
    });
    
    try {
      const result = await checklistsContext.getChecklists({
        page,
        per_page: 20,
      });
      
      const newTemplates = result?.checklists || [];
      
      if (reset) {
        setChecklistTemplates(newTemplates);
      } else {
        setChecklistTemplates(prev => [...prev, ...newTemplates]);
      }
      
      setChecklistHasMore(newTemplates.length === 20);
      setChecklistPage(page);
    } catch (error) {
      console.error('Error fetching checklist templates:', error);
    } finally {
      setLoadingChecklists(false);
    }
  }, [user, checklistsContext]);

  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchChecklistTemplates(0, true);
    }
  }, [user]);

  // Load more on scroll
  const handleChecklistScroll = (e) => {
    if (!user) return;
    const { target } = e;
    const scrolledToBottom = target.scrollHeight - target.scrollTop === target.clientHeight;
    
    if (scrolledToBottom && checklistHasMore && !loadingChecklists) {
      fetchChecklistTemplates(checklistPage + 1, false);
    }
  };

  const openAddCheck = () => {
    setCheckMode("add");
    setCheckInitial({
      _uiId: utilContext.genUUID(),
      label: "",
      item_type: "yes_no",
      config: null,
      is_required: true,
      require_photo: false,
      require_note: false,
      description: "",
    });
    setCheckOpen(true);
  };

  const openEditCheck = (existingCheck) => {
    setCheckMode("edit");
    setCheckInitial(existingCheck);
    setCheckOpen(true);
  };

  const openEditCheckFromItem = (item) => {
    setCheckMode("edit");
    setCheckInitial({
      id: item.id,
      _uiId: item._uiId || utilContext.genUUID(),
      label: item.label,
      item_type: item.item_type || "yes_no",
      config: item.config || null,
      is_required: item.is_required ?? true,
      require_photo: item.require_photo ?? false,
      require_note: item.require_note ?? false,
      description: item.description || "",
    });
    setCheckOpen(true);
  };

  const propertyOptions = rentalContext.rentals.map((rental) => {
    return {
      value: rental.rental_id,
      label: rental.pms_id
        ? `${rental.pms_id}, ${rental.address}`
        : `${rental.address}`,
    };
  });

  const guestOptions = reservationContext.listReservations.map(
    (reservation) => {
      return {
        value: reservation.guest_reservation_id,
        label: `${reservation.first_name} ${reservation.last_name}`,
      };
    }
  );

  const managerOptions = useMemo(() => {
    // Prefer availability data if available
    const fromAvailability = (availability || [])
      .filter(avail => avail.assignee_type === 'recipient')
      .map((avail) => ({
        label: avail.name || `Recipient ${avail.assignee_id}`,
        value: String(avail.assignee_id),
        recipient_id: avail.assignee_id,
      }));
    
    if (fromAvailability.length === 0) {
      // Fallback to context attributes
      return (workOrderContext.attributes?.assigners?.recipients || [])
        .map((r) => ({
          label: `${r.first_name} ${r.last_name}`,
          value: String(r.recipient_id),
          recipient_id: r.recipient_id,
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1));
    }
    return fromAvailability.sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [availability, workOrderContext.attributes]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
  });

  const isRecurringEdit =
    !isNew &&
    Boolean(workOrder?.task_recurrence_rule_id || recurrence?.rule_id);
  const recurrenceSyncKey = String(recurrence?._syncKey || "");
  const recurrenceLoading =
    showRecurrence &&
    !isNew &&
    Boolean(workOrder?.task_recurrence_rule_id) &&
    recurrenceSyncKey.startsWith("loading-");
  const recurrenceLoadFailed =
    showRecurrence &&
    !isNew &&
    Boolean(workOrder?.task_recurrence_rule_id) &&
    recurrenceSyncKey.startsWith("fallback-");
  const seriesFieldsEditable = !(
    isRecurringEdit &&
    recurringEditScope === "this"
  );

  const seedPropertyFromRentalId = useCallback(
    (rentalId) => {
      if (!rentalId) return;
      const numId = Number(rentalId);
      setValue("rental_id", String(numId));
      const seededRental = rentalContext?.rentals?.find(
        (r) => +r.rental_id === numId
      );
      if (seededRental) {
        setProperty({
          pms_id: seededRental.pms_id,
          name: seededRental.name,
          address: seededRental.address,
          rental_id: seededRental.rental_id,
        });
      }
    },
    [rentalContext?.rentals, setValue]
  );

  const taskType = watch("task_type");
  const [selectedTaskType, setSelectedTaskType] = useState("");
  const initializedOpenVersionRef = useRef(0);
  const genUUIDRef = useRef(utilContext.genUUID);

  useEffect(() => {
    setSelectedTaskType(taskType || "");
  }, [taskType]);

  useEffect(() => {
    genUUIDRef.current = utilContext.genUUID;
  }, [utilContext.genUUID]);

  const handleTaskTypeSelect = useCallback((value) => {
    setSelectedTaskType(value);
    setValue("task_type", value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    clearErrors("task_type");
  }, [clearErrors, setValue, isNew]);

  // Watch manager_id to filter from assignees (must be after useForm)
  const currentManagerId = watch("manager_id");

  // Legacy assignee dropdown options (grouped by Staff / Contact)
  const assignerOptions = useMemo(
    () => {
      const managerValue = currentManagerId ? `recipient_${currentManagerId}` : null;
      
      // Prefer availability data if available
      const recipientsFromAvail = (availability || [])
        .filter(avail => avail.assignee_type === 'recipient')
        .filter(avail => !managerValue || `recipient_${avail.assignee_id}` !== managerValue)
        .map((avail) => ({
          label: avail.name || `Recipient ${avail.assignee_id}`,
          value: `recipient_${avail.assignee_id}`,
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1));
      
      const contactsFromAvail = (availability || [])
        .filter(avail => avail.assignee_type === 'contact')
        .map((avail) => {
          const baseName = avail.name || `Contact ${avail.assignee_id}`;
          const cat = avail.category ? avail.category.charAt(0).toUpperCase() + avail.category.slice(1) : '';
          return {
            label: cat ? `${baseName} (${cat})` : baseName,
            value: `contact_${avail.assignee_id}`,
          };
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1));
      
      if (recipientsFromAvail.length === 0 && contactsFromAvail.length === 0) {
        // Fallback to context attributes
        return [
          {
            label: "Staff",
            options: (workOrderContext.attributes.assigners.recipients || [])
              .filter(recipient => !managerValue || `recipient_${recipient.recipient_id}` !== managerValue)
              .sort((a, b) => (a.first_name > b.first_name ? 1 : -1))
              .map((recipient) => ({
                label: `${recipient.first_name} ${recipient.last_name}`,
                value: `recipient_${recipient.recipient_id}`,
              })),
          },
          {
            label: "Contact",
            options: (workOrderContext.attributes.assigners.contacts || [])
              .sort((a, b) => (a.first_name > b.first_name ? 1 : -1))
              .map((contact) => {
                const cat = contact.category ? contact.category.charAt(0).toUpperCase() + contact.category.slice(1) : '';
                const baseName = `${contact.first_name} ${contact.last_name}`;
                return {
                  label: cat ? `${baseName} (${cat})` : baseName,
                  value: `contact_${contact.id}`,
                };
              }),
          },
        ];
      }
      
      return [
        {
          label: "Staff",
          options: recipientsFromAvail,
        },
        {
          label: "Contact",
          options: contactsFromAvail,
        },
      ];
    },
    [availability, workOrderContext.attributes, currentManagerId]
  );

  // Build assignee options purely from the availability API response
  const availabilityAssignees = useMemo(() => {
    const managerValue = currentManagerId ? `recipient_${currentManagerId}` : null;
    
    return (availability || [])
      .filter(avail => {
        const availValue = `${avail.assignee_type}_${avail.assignee_id}`;
        return !managerValue || availValue !== managerValue;
      })
      .map((avail) => {
        const isContact = avail.assignee_type === 'contact';
        const baseName = avail.name || `${avail.assignee_type}_${avail.assignee_id}`;
        const cat = isContact && avail.category ? avail.category.charAt(0).toUpperCase() + avail.category.slice(1) : '';
        return {
          group: isContact ? 'Contact' : 'Staff',
          label: cat ? `${baseName} (${cat})` : baseName,
          value: `${avail.assignee_type}_${avail.assignee_id}`,
          type: avail.assignee_type,
          recipient_id: avail.assignee_type === 'recipient' ? avail.assignee_id : undefined,
          contact_id: isContact ? avail.assignee_id : undefined,
        };
      });
  }, [availability, currentManagerId]);

  // Merge availability API assignees into the dropdown so that picks from
  // the AssigneePickerModal always display their name instead of the raw value
  const mergedAssignerOptions = useMemo(() => {
    const managerValue = currentManagerId ? `recipient_${currentManagerId}` : null;
    
    const staffVals = new Set(
      (assignerOptions[0]?.options || []).map((o) => o.value)
    );
    const contactVals = new Set(
      (assignerOptions[1]?.options || []).map((o) => o.value)
    );

    const extraStaff = [];
    const extraContacts = [];
    (availabilityAssignees || []).forEach((a) => {
      if (a.type === "recipient" && !staffVals.has(a.value)) {
        extraStaff.push({ label: a.label, value: a.value });
      } else if (a.type === "contact" && !contactVals.has(a.value)) {
        extraContacts.push({ label: a.label, value: a.value });
      }
    });

    return [
      {
        label: "Staff",
        options: [...(assignerOptions[0]?.options || []), ...extraStaff],
      },
      {
        label: "Contact",
        options: [...(assignerOptions[1]?.options || []), ...extraContacts],
      },
    ];
  }, [assignerOptions, availabilityAssignees, currentManagerId]);

  // Helper: compute duration in minutes from two HH:MM strings
  const durationFromTimes = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    if ([sh, sm, eh, em].some(Number.isNaN)) return null;
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    return diff > 0 ? diff : null;
  };

  // Helper: compute HH:MM end time from start + duration
  const computeEndTimeStr = (startTime, durationMins) => {
    if (!startTime) return null;
    const dur = Number(durationMins || 0);
    if (!dur) return null;
    const [h, m] = startTime.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const d = new Date();
    d.setHours(h, m, 0, 0);
    d.setMinutes(d.getMinutes() + dur);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  // Snap a duration (in minutes) to the closest option in the dropdown
  const snapDuration = (mins) => {
    const opts = [30, 60, 90, 120, 180, 240];
    if (!mins || mins <= 0) return "60";
    let best = opts[0];
    for (const o of opts) {
      if (Math.abs(o - mins) < Math.abs(best - mins)) best = o;
    }
    return String(best);
  };

  const openAssignPicker = async () => {
    setAssignDraft(assignList || []);
    setFocusedAssignee(null);

    // Use existing schedule if available, otherwise default to "now"
    if (existingSchedule && existingSchedule.scheduled_date) {
      // Calculate day offset from today to scheduled date
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const scheduledDate = new Date(existingSchedule.scheduled_date + 'T00:00:00');
      const diffTime = scheduledDate.getTime() - today.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      
      setAssignPickerDayOffset(diffDays);
      
      // Use existing schedule data for the picker
      setAssignScheduleStartDate(existingSchedule.scheduled_date.slice(0, 10));
      setAssignScheduleStartTime(existingSchedule.scheduled_start_time?.slice(0, 5) || "09:00");
      
      // Calculate duration from start/end times if available
      if (existingSchedule.scheduled_start_time && existingSchedule.scheduled_end_time) {
        const dur = durationFromTimes(
          existingSchedule.scheduled_start_time.slice(0, 5),
          existingSchedule.scheduled_end_time.slice(0, 5)
        );
        setAssignScheduleDuration(dur ? snapDuration(dur) : "60");
      } else {
        setAssignScheduleDuration("60");
      }
    } else {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mon = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const fallbackDate = `${yyyy}-${mon}-${dd}`;
      const targetDate = assignScheduleStartDate || fallbackDate;
      const scheduledDate = new Date(`${targetDate}T00:00:00`);
      const today = new Date(`${fallbackDate}T00:00:00`);
      const diffDays = Math.round(
        (scheduledDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );

      setAssignPickerDayOffset(diffDays);
      setAssignScheduleStartDate(targetDate);

      if (!assignScheduleStartTime) {
        const hh = now.getHours();
        const rawMm = now.getMinutes();
        const snapped = Math.ceil(rawMm / 15) * 15;
        const finalHh = snapped >= 60 ? String((hh + 1) % 24).padStart(2, "0") : String(hh).padStart(2, "0");
        const finalMm = snapped >= 60 ? "00" : String(snapped).padStart(2, "0");
        setAssignScheduleStartTime(`${finalHh}:${finalMm}`);
      }

      if (!assignScheduleDuration) {
        setAssignScheduleDuration("60");
      }
    }

    setAssignPickerOpen(true);
  };

  const closeAssignPicker = () => {
    setAssignPickerOpen(false);
  };

  const refreshWorkOrderAfterSave = async (workOrderId) => {
    try {
      const freshWO = await WorkOrderService.getWorkOrder(workOrderId);
      if (freshWO?.data) {
        workOrderContext.setWorkOrder(freshWO.data);
        workOrderContext.setWorkOrders?.((prev) =>
          (prev || []).map((wo) => (+wo.id === +workOrderId ? freshWO.data : wo))
        );
      }
    } catch (err) {
      console.log("Failed to refresh work order:", err);
    }
    await onSaved?.();
  };

  const applyAssignPicker = async () => {
    // Build the assigners array from the draft selections
    const currentManagerId_ = getValues("manager_id");
    const managerValue = currentManagerId_ ? `recipient_${currentManagerId_}` : null;

    const assigners = (assignDraft || [])
      .filter(sl => !managerValue || sl !== managerValue)
      .map((sl) => {
        const value = typeof sl === "string" ? sl : sl.value;
        return {
          type: value.split("_")[0],
          id: value.split("_")[1],
          assignment_role: 'assignee',
        };
      });

    // Add manager as owner if selected
    if (currentManagerId_) {
      assigners.unshift({
        type: 'recipient',
        id: currentManagerId_,
        assignment_role: 'owner',
      });
    }

    const endTime = computeEndTimeStr(assignScheduleStartTime, assignScheduleDuration);
    const effectiveEnd = endTime || assignScheduleStartTime;

    // Replace assignList with whatever was selected in the modal
    setAssigns(assignDraft || []);

    // Update the form's start_date if a schedule date was selected
    if (assignScheduleStartDate) {
      setValue("start_date", assignScheduleStartDate);
    }

    // Save schedule if this is an existing work order
    if (!isNew && workOrder?.id && assignScheduleStartDate && assignScheduleStartTime) {
      const schedulePayload = {
        scheduled_date: assignScheduleStartDate,
        scheduled_start_time: assignScheduleStartTime,
        scheduled_end_time: effectiveEnd,
        scheduled_by: user?.id || user?.user_id || null,
        assigners,
      };

      const recurring = isRecurringWorkOrder(workOrder);
      let editScope = "this";
      if (recurring) {
        let scope = recurringEditScope;
        if (scope) {
          setRecurringEditScope(null);
        } else {
          scope = await promptRecurringEditScope({
            itemLabel,
            summary: workOrder?.summary,
          });
        }
        if (!scope) return;
        editScope = scope;
      }

      try {
        if (recurring && (editScope === "future" || editScope === "all")) {
          const recurrenceRuleId =
            workOrder.task_recurrence_rule_id || recurrence?.rule_id || null;
          if (!recurrenceRuleId) {
            UIkit.notification(
              "Unable to identify recurrence rule for this task. Please close and reopen.",
              "danger"
            );
            return;
          }

          const formValues = getValues();
          const existingRentalIds = Array.isArray(recurrence?.rental_ids)
            ? recurrence.rental_ids
            : [];
          const rentalIds = recurrence?.rental_scope === "all_rentals"
            ? []
            : formValues.rental_id
              ? [Number(formValues.rental_id)]
              : existingRentalIds.length
                ? existingRentalIds
                : workOrder.rental_id
                  ? [Number(workOrder.rental_id)]
                  : [];

          const futureStartDate = getOccurrenceStartDate(
            workOrder,
            existingSchedule,
            assignScheduleStartDate
          );
          if (editScope === "future" && !futureStartDate) {
            UIkit.notification(
              "Unable to determine this occurrence's date. Please close and reopen the task.",
              "danger"
            );
            return;
          }

          const rulePayload = buildFutureRuleUpdatePayload({
            recurrence,
            data: {
              ...formValues,
              summary: formValues.summary || workOrder.summary,
              assigners,
            },
            assignScheduleStartTime,
            existingScheduleStartTime:
              existingSchedule?.scheduled_start_time || workOrder?.scheduled_start_time,
            assignScheduleDuration,
            selectedChecklistTemplate,
            checklistTemplateTouched,
            rentalIds,
            timePreference: "schedule",
            editMode: editScope === "all" ? "all" : "future_only",
          });

          if (editScope === "all") {
            await TaskRecurrenceRuleService.update(recurrenceRuleId, rulePayload);
          } else {
            const splitRes = await TaskRecurrenceRuleService.split(recurrenceRuleId, {
              effective_date: futureStartDate,
              payload: rulePayload,
            });
            const newRuleId = splitRes?.data?.new_rule?.id;
            if (newRuleId) {
              setRecurrence((prev) => ({
                ...(prev || {}),
                rule_id: newRuleId,
                _syncKey: `rule-${newRuleId}`,
              }));
            }
          }
          UIkit.notification(
            editScope === "all"
              ? "All upcoming occurrences updated"
              : "All future occurrences updated",
            "success"
          );

          if (workOrder?.id) {
            await refreshWorkOrderAfterSave(workOrder.id);
          }
          refreshAvailabilityForDate(assignScheduleStartDate);
          workSchedulesCtx?.notifyScheduleChange?.(assignScheduleStartDate);
          closeAssignPicker();
          return;
        }

        let result;
        if (existingSchedule) {
          result = await updateWorkOrderSchedule(workOrder.id, schedulePayload);
        } else {
          result = await createWorkOrderSchedule(workOrder.id, schedulePayload);
          setExistingSchedule(schedulePayload);
        }

        if (result) {
          workOrderContext.setWorkOrder({ ...workOrder, ...result });
        }

        await refreshWorkOrderAfterSave(workOrder.id);
        UIkit.notification("Schedule saved", "success");
        refreshAvailabilityForDate(assignScheduleStartDate);
        workSchedulesCtx?.notifyScheduleChange?.(assignScheduleStartDate);
      } catch (err) {
        const data = err?.response?.data;
        let msg = data?.error || "Failed to save schedule";
        if (data?.conflicts && Array.isArray(data.conflicts)) {
          msg = data.conflicts.join("; ");
        }
        UIkit.notification(msg, { status: "danger", timeout: 7000 });
        console.log("Schedule save error:", err);
        return;
      }
    }

    closeAssignPicker();
  };

  const toggleAssignDraft = (value) => {
    setAssignDraft((prev) => {
      const arr = prev || [];
      const exists = arr.includes(value);

      if (exists) return arr.filter((v) => v !== value);
      return [...arr, value];
    });

    // Auto-focus on selection
    const opt = availabilityAssignees.find((o) => o.value === value);
    if (opt) setFocusedAssignee(opt);
  };

  // Use assignScheduleStartDate directly as the date for fetching availability
  const dayISO = useMemo(() => {
    return assignScheduleStartDate || (() => {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    })();
  }, [assignScheduleStartDate]);

  // Helper: add N days to an ISO date string and return a new ISO string
  const addDaysISO = useCallback((isoDate, n) => {
    const d = new Date(`${isoDate}T00:00:00`);
    d.setDate(d.getDate() + n);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  // Merge new backend availability data into existing state
  const mergeAvailability = useCallback((newData) => {
    setAvailability((prev) => {
      if (!prev || prev.length === 0) return newData;

      const merged = new Map();

      // Index existing assignees
      for (const a of prev) {
        merged.set(`${a.assignee_type}_${a.assignee_id}`, {
          ...a,
          availability: [...(a.availability || [])],
        });
      }

      // Merge new data
      for (const a of newData) {
        const key = `${a.assignee_type}_${a.assignee_id}`;
        const existing = merged.get(key);
        if (!existing) {
          merged.set(key, { ...a, availability: [...(a.availability || [])] });
        } else {
          // Merge availability dates — new data wins for overlapping dates
          const dateMap = new Map();
          for (const d of existing.availability) dateMap.set(d.date, d);
          for (const d of (a.availability || [])) dateMap.set(d.date, d);
          existing.availability = Array.from(dateMap.values()).sort((x, y) =>
            x.date.localeCompare(y.date)
          );
        }
      }

      return Array.from(merged.values());
    });
  }, []);

  // Fetch a specific date range and merge into cache
  const fetchAvailabilityRange = useCallback(async (fetchStart, fetchEnd) => {
    if (fetchingAvailabilityRef.current) return;
    fetchingAvailabilityRef.current = true;
    try {
      const data = await workSchedulesCtxRef.current.getAvailability(
        fetchStart, fetchEnd
      );
      const newData = Array.isArray(data) ? data : [];
      if (newData.length > 0) {
        mergeAvailability(newData);
      }

      // Expand cached range
      const prev = availCacheRangeRef.current;
      if (!prev.start) {
        availCacheRangeRef.current = { start: fetchStart, end: fetchEnd };
      } else {
        availCacheRangeRef.current = {
          start: fetchStart < prev.start ? fetchStart : prev.start,
          end: fetchEnd > prev.end ? fetchEnd : prev.end,
        };
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      fetchingAvailabilityRef.current = false;
    }
  }, [mergeAvailability]);

  // Force-refresh availability for a specific date (bypasses in-flight guard).
  // Used after scheduling changes so the UI immediately reflects the new state.
  const refreshAvailabilityForDate = useCallback(async (isoDate) => {
    if (!isoDate) return;
    try {
      const data = await workSchedulesCtxRef.current.getAvailability(
        isoDate, isoDate
      );
      const newData = Array.isArray(data) ? data : [];
      if (newData.length > 0) {
        mergeAvailability(newData);
      }
    } catch (error) {
      console.error("Error refreshing availability for date:", isoDate, error);
    }
  }, [mergeAvailability]);

  // Ensure a given date is covered by the cache; if not, fetch the gap
  const ensureDateCached = useCallback(async (isoDate) => {
    if (!isoDate) return;
    const cached = availCacheRangeRef.current;

    if (cached.start && cached.end && isoDate >= cached.start && isoDate <= cached.end) {
      // Already cached — nothing to do
      return;
    }

    if (!cached.start) {
      // No cache at all — fetch 7 days forward from the requested date
      const fetchStart = isoDate;
      const fetchEnd = addDaysISO(isoDate, 6);
      await fetchAvailabilityRange(fetchStart, fetchEnd);
    } else if (isoDate > cached.end) {
      // Navigating forward — fetch from day after cache end to requested date + 6 days
      const fetchStart = addDaysISO(cached.end, 1);
      const fetchEnd = addDaysISO(isoDate, 6);
      await fetchAvailabilityRange(fetchStart, fetchEnd);
    } else if (isoDate < cached.start) {
      // Navigating backward — fetch from requested date to day before cache start
      const fetchStart = isoDate;
      const fetchEnd = addDaysISO(cached.start, -1);
      if (fetchStart <= fetchEnd) {
        await fetchAvailabilityRange(fetchStart, fetchEnd);
      }
    }
  }, [addDaysISO, fetchAvailabilityRange]);

  // Initial fetch: today + 6 days (7 days total)
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const todayISO = `${yyyy}-${mm}-${dd}`;
    const endISO = addDaysISO(todayISO, 6);
    fetchAvailabilityRange(todayISO, endISO);
  }, [addDaysISO, fetchAvailabilityRange]);

  // When the user navigates to a different day, ensure it's cached
  useEffect(() => {
    if (assignPickerOpen && dayISO) {
      ensureDateCached(dayISO);
    }
  }, [assignPickerOpen, dayISO, ensureDateCached]);

  const getAssigneeSchedule = useCallback((assignee, isoDate) => {
    if (!assignee || !isoDate) return buildScheduleFromDateAvail(null);

    // Match by composite key — assignees are sourced from the same availability data
    const assigneeAvail = availability.find(
      (a) => `${a.assignee_type}_${a.assignee_id}` === assignee.value
    );
    if (!assigneeAvail) return buildScheduleFromDateAvail(null);

    const dateAvail = assigneeAvail.availability?.find(
      (d) => d.date === isoDate
    );

    return buildScheduleFromDateAvail(dateAvail);
  }, [availability]);

  useEffect(() => {
    return () => {
      debounceSearchGuest.cancel();
      debounceSearchRental.cancel();
      // handleClearForm();
    };
  }, []);

  const editWorkOrderKey = !isNew && workOrder
    ? (workOrder.id || workOrder.occurrence_key || workOrder.task_recurrence_rule_id || "edit")
    : null;

  useEffect(() => {
    if (!isNew && workOrder) {
      const fields = [
        "summary",
        ...(showTaskType ? ["task_type"] : []),
        "description",
        "priority",
        "status",
        "guest_reservation_id",
        "rental_id",
        "tags",
        "category",
      ];
      fields.forEach((field) => {
        // Existing pre-Tasks rows have task_type = NULL. Treat them as
        // 'work_order' in the UI so the Type select shows a sensible value
        // without requiring a data backfill on the ~5,000 legacy rows.
        if (field === "task_type" && !workOrder[field]) {
          setValue(field, workOrder.type === "task" ? "" : "");
          return;
        }
        if (field === "rental_id" && workOrder[field] != null) {
          setValue(field, String(workOrder[field]));
          return;
        }
        setValue(field, workOrder[field]);
      });

      const attachments = workOrder?.attachments?.filter(
        (attachment) => attachment.type === "image"
      );
      setAttachments(attachments || []);

      // Separate owner (manager) from regular assignees
      const allAssignees = workOrder?.work_order_assignees || [];
      const owner = allAssignees.find(a => a.assignment_role === 'owner');
      const regularAssignees = allAssignees.filter(a => a.assignment_role !== 'owner');

      // Set manager if owner exists
      if (owner && owner.assignee_type === 'recipient') {
        setValue('manager_id', String(owner.assignee_id));
      } else {
        setValue('manager_id', null);
      }

      const assigners = regularAssignees.map(
        (assigner) => `${assigner.assignee_type}_${assigner.assignee_id}`
      );

      setAssigns(assigners);

      // Load checklist items from work order response
      if (workOrder.checklist_items && Array.isArray(workOrder.checklist_items)) {
        const checklistItems = normalizeItems(workOrder.checklist_items, genUUIDRef.current);
        setItems(checklistItems);
        
        // Set template if one was used
        if (workOrder.checklist_instance?.template_id) {
          setSelectedChecklistTemplate(workOrder.checklist_instance.template_id);
          setChecklistTemplateTouched(false);
        }
      } else {
        setItems([]);
        if (!workOrder?.isVirtual) {
          setSelectedChecklistTemplate("");
          setChecklistTemplateTouched(false);
        }
      }

      // Fetch existing schedule for this work order
      const fetchScheduleForWorkOrder = async () => {
        if (!workOrder?.id) {
          setExistingSchedule(null);
          return;
        }
        try {
          const schedule = await fetchWorkOrderSchedule(workOrder.id);
          setExistingSchedule(schedule || null);
          if (schedule?.scheduled_date) {
            setAssignScheduleStartDate(String(schedule.scheduled_date).slice(0, 10));
            setAssignScheduleStartTime(schedule.scheduled_start_time?.slice(0, 5) || "09:00");
            if (schedule.scheduled_start_time && schedule.scheduled_end_time) {
              const dur = durationFromTimes(
                schedule.scheduled_start_time.slice(0, 5),
                schedule.scheduled_end_time.slice(0, 5)
              );
              setAssignScheduleDuration(dur ? snapDuration(dur) : "60");
            } else {
              setAssignScheduleDuration("60");
            }
          }
        } catch (err) {
          console.log("Failed to fetch schedule:", err);
          setExistingSchedule(null);
        }
      };
      fetchScheduleForWorkOrder();

      if (workOrder.scheduled_date) {
        setAssignScheduleStartDate(String(workOrder.scheduled_date).slice(0, 10));
      }
      if (workOrder.scheduled_start_time) {
        setAssignScheduleStartTime(String(workOrder.scheduled_start_time).slice(0, 5));
      }
      if (workOrder.scheduled_start_time && workOrder.scheduled_end_time) {
        const dur = durationFromTimes(
          String(workOrder.scheduled_start_time).slice(0, 5),
          String(workOrder.scheduled_end_time).slice(0, 5)
        );
        setAssignScheduleDuration(dur ? snapDuration(dur) : "60");
      }

      if (showRecurrence) {
        const ruleId = workOrder.task_recurrence_rule_id;
        if (ruleId) {
          setRecurrence((prev) => ({
            ...(prev || {}),
            enabled: true,
            rule_id: ruleId,
            _syncKey: `loading-${ruleId}-${workOrder.id || workOrder.occurrence_key || "virtual"}`,
          }));
          TaskRecurrenceRuleService.get(ruleId)
            .then((res) => {
              const rule = res?.data?.rule || res?.data;
              setRecurrence(recurrenceValueFromRule(rule));
            })
            .catch((err) => {
              console.log("Failed to fetch recurrence rule:", err);
              setRecurrence({ enabled: true, _syncKey: `fallback-${ruleId}-${workOrder.id}` });
            });
        } else {
          setRecurrence({ enabled: false, _syncKey: `one-time-${workOrder.id}` });
        }
      }
    }
    // This effect intentionally initializes editable form state once per
    // opened work order. Provider/list churn must not replay it and clobber
    // unsaved edits in the shared standalone work-order form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editWorkOrderKey, isNew, formOpenVersion]);

  useEffect(() => {
    if (isNew || !workOrder) return;

    if (workOrder.guest_reservation) {
      setGuest(workOrder.guest_reservation);
      return;
    }

    if (!workOrder.guest_reservation_id) return;
    const reservation = reservationContext.listReservations.find(
      (gr) => Number(gr.guest_reservation_id) === Number(workOrder.guest_reservation_id)
    );
    if (reservation) {
      setGuest(reservation);
    }
  }, [
    isNew,
    reservationContext.listReservations,
    workOrder?.guest_reservation,
    workOrder?.guest_reservation_id,
  ]);

  useEffect(() => {
    if (isNew || !workOrder) return;

    if (workOrder.rental) {
      setProperty(workOrder.rental);
      return;
    }

    const rentalId = workOrder.rental_id || recurrence?.rental_ids?.[0];
    if (!rentalId) return;
    const seededRental = rentalContext?.rentals?.find(
      (r) => Number(r.rental_id) === Number(rentalId)
    );
    if (seededRental) {
      setProperty({
        pms_id: seededRental.pms_id,
        name: seededRental.name,
        address: seededRental.address,
        rental_id: seededRental.rental_id,
      });
    }
  }, [
    isNew,
    recurrence?.rental_ids,
    rentalContext?.rentals,
    workOrder?.rental,
    workOrder?.rental_id,
    workOrder,
  ]);

  useEffect(() => {
    if (!isNew) {
      initializedOpenVersionRef.current = 0;
      return;
    }
    if (!formOpenVersion) return;
    if (initializedOpenVersionRef.current === formOpenVersion) return;
    initializedOpenVersionRef.current = formOpenVersion;

    handleClearForm();

    // Prefill from a seeded workOrder. The Tasks Calendar pushes
    // { rental_id, scheduled_date } into the context before opening
    // the modal so clicking an empty cell lands the user on a form
    // pre-filled with that property and date. We only honour seeds
    // when the corresponding field is non-empty so a freshly-init'd
    // blank workOrder doesn't accidentally try to prefill.
    if (workOrder?.rental_id) {
      setValue("rental_id", String(workOrder.rental_id));
      seedPropertyFromRentalId(workOrder.rental_id);
    }
    if (workOrder?.scheduled_date) {
      const seededDate = String(workOrder.scheduled_date).slice(0, 10);
      setAssignScheduleStartDate(seededDate);
      setAssignScheduleStartTime("09:00");
      setAssignScheduleDuration("60");
      setRecurrence({
        enabled: false,
        _syncKey: `calendar-seed-${seededDate}`,
        ...recurrenceSeedFromScheduledDate(seededDate),
      });
    }

    if (grId) {
      const reservation = reservationContext.listReservations.find(
        (gr) => Number(gr.guest_reservation_id) === Number(grId)
      );
      if (reservation) {
        setValue("guest_reservation_id", reservation.guest_reservation_id);
        setGuest(reservation);

        if (reservation.rental_id) {
          setValue("rental_id", reservation.rental_id);
          setProperty({
            pms_id: reservation.pms_id,
            name: reservation.rental_name,
            address: reservation.rental_address,
            rental_id: reservation.rental_id,
          });
        }
      }
    }

    if (msgId) {
      setValue("message_id", msgId);
      if (msg_gr) {
        setValue("guest_reservation_id", msg_gr.guest_reservation_id);
        setGuest(msg_gr);

        if (msg_gr.rental_id) {
          setValue("rental_id", msg_gr.rental_id);
          setProperty({
            pms_id: msg_gr.pms_id,
            name: msg_gr.rental_name,
            address: msg_gr.pms_address,
            rental_id: msg_gr.rental_id,
          });
        }
      }
    }
  }, [
    formOpenVersion,
    grId,
    isNew,
    msgId,
    msg_gr,
    reservationContext.listReservations,
    seedPropertyFromRentalId,
    setValue,
    workOrder,
  ]);

  // Virtual occurrences have no concrete work_order_assignees rows, so the
  // main population effect above leaves the form empty. Repopulate the
  // assignee picker and manager from the rule's default_assignees once the
  // rule has loaded, so editing a virtual occurrence doesn't submit an empty
  // assigner set (which would otherwise wipe assignees on materialize/update).
  useEffect(() => {
    if (isNew) return;
    if (!workOrder?.isVirtual) return;
    if (assignList.length) return; // user already has selections; don't clobber
    const defaults = recurrence?.default_assignees;
    if (!Array.isArray(defaults) || !defaults.length) return;

    const owner = defaults.find((a) => a.assignment_role === "owner");
    const regular = defaults.filter((a) => a.assignment_role !== "owner");
    if (owner && owner.type === "recipient") {
      setValue("manager_id", String(owner.id));
    }
    if (regular.length) {
      setAssigns(regular.map((a) => `${a.type}_${a.id}`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurrence?.rule_id, recurrence?._syncKey, workOrder?.isVirtual]);

  useEffect(() => {
    const syncKey = String(recurrence?._syncKey || "");
    if (isNew || !showRecurrence) return;
    if (!recurrence?.rule_id || !recurrence?.mode) return;
    if (syncKey.startsWith("loading-") || syncKey.startsWith("fallback-")) return;
    if (checklistTemplateTouched) return;

    const templateId = recurrence?.checklist_template_id || "";
    setSelectedChecklistTemplate(templateId);

    if (!templateId) {
      if (workOrder?.isVirtual || !Array.isArray(workOrder?.checklist_items)) {
        setItems([]);
      }
      return;
    }

    if (
      !workOrder?.isVirtual &&
      workOrder?.checklist_instance?.template_id === templateId &&
      Array.isArray(workOrder?.checklist_items) &&
      workOrder.checklist_items.length
    ) {
      return;
    }

    checklistsContext.getChecklist(templateId)
      .then((checklistData) => {
        setItems(normalizeItems(checklistData?.items || [], genUUIDRef.current));
      })
      .catch((error) => {
        console.error("Error loading checklist template:", error);
      });
  }, [
    checklistTemplateTouched,
    checklistsContext,
    isNew,
    recurrence?._syncKey,
    recurrence?.checklist_template_id,
    recurrence?.mode,
    recurrence?.rule_id,
    showRecurrence,
    workOrder,
  ]);

  const convertedGuest = useMemo(() => {
    if (guest?.check_in?.includes("T")) {
      return guest;
    } else {
      return {
        ...guest,
        check_in: guest?.check_in + "T00:00:00.000Z",
        check_out: guest?.check_out + "T00:00:00.000Z",
      };
    }
  }, [guest]);

  const tags = useMemo(() => getValues("tags") || [], [watch("tags")]);

  const handleSearchGuests = (evt) => {
    reservationContext.searchReservation({
      search_term: evt.target.value,
      filters: "all",
      preset: "soon",
      start_date: "2000-01-01",
      end_date: "2050-12-31",
      end_type: "check_in",
      page: 0,
      checked_in: false,
      checked_out: false,
      categories: "",
    });
  };

  const debounceSearchGuest = useMemo(
    () => debounce(handleSearchGuests, 500),
    []
  );

  const handleSearchRentals = (evt) => {
    rentalContext.searchRentals({
      search_term: evt.target.value,
    });
  };

  const debounceSearchRental = useMemo(
    () => debounce(handleSearchRentals, 500),
    []
  );

  const handleSelectGuest = (option) => {
    const reservation = reservationContext.listReservations.find(
      (gr) => gr.guest_reservation_id === option.value
    );
    setValue("guest_reservation_id", reservation.guest_reservation_id);
    setGuest(reservation);
    setGuestKey(null);
    reservationContext.setReservations([]);

    const property = rentalContext.rentals.find(
      (pr) => pr.pms_id === reservation.pms_id
    );
    setValue("rental_id", property?.rental_id);
    setProperty(property);
    setPropertyKey(null);
  };

  const handleSelectProperty = (option) => {
    const property = rentalContext.rentals.find(
      (pr) => pr.rental_id === option.value
    );
    setValue("rental_id", property?.rental_id != null ? String(property.rental_id) : null);
    setProperty(property);
    setPropertyKey(null);
    clearErrors("rental_id");
    // setGuest(null);
  };

  // Remove manager from assignList if manager changes and is currently in assignees
  useEffect(() => {
    if (currentManagerId) {
      const managerValue = `recipient_${currentManagerId}`;
      setAssigns(prev => prev.filter(a => {
        const value = typeof a === "string" ? a : a.value;
        return value !== managerValue;
      }));
    }
  }, [currentManagerId]);

  const handleClearForm = useCallback(() => {
    setRecurringEditScope?.(null);
    setGuestKey(null);
    setPropertyKey(null);
    setProperty(null);
    setGuest(null);
    setAssigns([]);
    setAttachments([]);
    setItems([]);
    setSelectedChecklistTemplate("");
    setChecklistTemplateTouched(false);
    setExistingSchedule(null);
    setAssignScheduleStartDate("");
    setAssignScheduleStartTime("");
    setAssignScheduleDuration("60");
    setAssignPickerDayOffset(0);
    setAssignDraft([]);
    setFocusedAssignee(null);
    setSelectedTaskType("");
    setRecurrence({ enabled: false, _syncKey: `clear-${workOrder?.id || "new"}` });
    clearErrors();
    reset();
  }, [clearErrors, reset, setRecurringEditScope, workOrder?.id]);
  clearFormRef.current = handleClearForm;

  const onPickChecklistTemplate = async (id) => {
    setChecklistTemplateTouched(true);
    setSelectedChecklistTemplate(id);
    if (!id) {
      setItems([]);
      return;
    }

    try {
      const checklistData = await checklistsContext.getChecklist(id);
      
      setItems(normalizeItems(checklistData.items || [], utilContext.genUUID));
    } catch (error) {
      console.error('Error loading checklist template:', error);
      UIkit.notification('Failed to load checklist template', { status: 'danger' });
    }
  };

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => (i._uiId || i.id) === active.id);
    const newIndex = items.findIndex((i) => (i._uiId || i.id) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = [...items];
    const [moved] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, moved);
    setItems(next);
  };

  const handleUpdateItem = (updated, index) => {
    const next = [...items];
    next[index] = updated;
    setItems(next);
  };

  const handleDeleteItem = (index) => {
    const next = [...items];
    next.splice(index, 1);
    setItems(next);
  };

  const handleRegister = useCallback(
    async (data) => {
      try {
        if (
          isNew &&
          showTaskType &&
          (data.rental_id == null || data.rental_id === "")
        ) {
          setError("rental_id", { type: "required", message: "Property is required" });
          return;
        }

        if (!showTaskType) {
          delete data.task_type;
        } else {
          data.type = "task";
          data.task_source = data.task_source || "manual";
        }

        const assigners = [];
        
        // Add manager as owner if selected
        if (data.manager_id) {
          assigners.push({
            type: 'recipient',
            id: data.manager_id,
            assignment_role: 'owner',
          });
        }
        
        // Add regular assignees (filter out manager if somehow present)
        if (assignList && assignList.length > 0) {
          const managerValue = data.manager_id ? `recipient_${data.manager_id}` : null;
          
          const regularAssigners = assignList
            .filter(sl => {
              const value = typeof sl === "string" ? sl : sl.value;
              return !managerValue || value !== managerValue;
            })
            .map((sl) => {
              const value = typeof sl === "string" ? sl : sl.value;
              return {
                type: value.split("_")[0],
                id: value.split("_")[1],
                assignment_role: 'assignee',
              };
            });
          
          assigners.push(...regularAssigners);
        }
        
        // Always include assigners so recurring rule edits can distinguish
        // "unchanged" from an intentional clear-all selection.
        data["assigners"] = assigners;

        if (attachmentList) {
          const files = [...attachmentList].filter((a) => !a?.url);

          const urls = await Promise.all(
            files.map((file) =>
              utilContext.uploadFile(
                file,
                `workorders/attachments/${utilContext.genUUID()}`
              )
            )
          );

          const newAssets = files.map((file, idx) => {
            return {
              type: "image",
              name: file.name,
              original_name: file.name,
              url: urls[idx],
            };
          });

          let attachments = attachmentList.map((a) => {
            if (a?.url) return a;
            return newAssets.shift();
          });

          data["attachments"] = attachments;
        }

        // Always send checklist object to allow clearing items when "None" is selected
        if (!isNew || items.length > 0 || selectedChecklistTemplate) {
          data["checklist"] = {
            template_id: selectedChecklistTemplate || null,
            items: items.map((item) => ({
              ...(item.id !== undefined ? { id: item.id } : {}),
              label: item.label,
              item_type: item.item_type,
              config: item.config || null,
              description: item.description || null,
              is_required: item.is_required ?? true,
              require_photo: item.require_photo ?? false,
              require_note: item.require_note ?? false
            }))
          };
        }

        // Recurring task: create a recurrence rule instead of a single
        // work_order. Backend engine writes work_orders rows asynchronously
        // (and we trigger one immediate /generate run for instant UX).
        // Editing an existing rule from this form is intentionally NOT
        // supported — the form only edits the work_order row that the
        // rule produced. Manage the rule itself from /operations/tasks/recurrence-rules.
        if (showRecurrence && isNew && recurrence?.enabled) {
          const rentalIds = data.rental_id ? [Number(data.rental_id)] : [];
          // Keep owner/manager and preserve assignment_role so generated and
          // virtual occurrences carry the full assignee set.
          const defaultAssignees = (data.assigners || [])
            .map((a) => ({
              type: a.type,
              id: Number(a.id),
              assignment_role: a.assignment_role || "assignee",
            }));

          const rulePayload = buildRulePayload(recurrence, {
            name: data.summary,
            summary: data.summary,
            task_type: data.task_type,
            priority: data.priority,
            category: data.category,
            description: data.description,
            rental_ids: rentalIds,
            default_assignees: defaultAssignees,
            checklist_template_id: selectedChecklistTemplate || null,
          });

          let ruleId = null;
          try {
            const ruleRes = await TaskRecurrenceRuleService.create(rulePayload);
            ruleId = ruleRes?.data?.rule?.id || ruleRes?.data?.id;
          } catch (e) {
            console.error("Recurrence rule create failed:", e);
            UIkit.notification("Failed to save recurring task", "danger");
            return;
          }

          closeWorkOrderAdd();
          UIkit.notification("Recurring task created", "success");
          await onSaved?.();
          workOrderContext.initWorkOrder();
          handleClearForm();
          setRecurrence({ enabled: false });
          return;
        }

        // Editing a task produced by a recurrence rule: ask whether the change
        // applies to only this occurrence or to all future occurrences.
        //   - "this"  → update just this work_order row. The backend update
        //               query sets recurrence_override = TRUE for
        //               task_source = 'recurrence', so the engine will no
        //               longer overwrite this occurrence.
        //   - "future"→ update the recurrence rule (handles cadence changes,
        //               e.g. weekly → monthly) and regenerate; the engine
        //               syncs every new, non-overridden future occurrence,
        //               including the clicked one.
        let recurrenceRuleId =
          workOrder?.task_recurrence_rule_id ||
          recurrence?.rule_id ||
          null;
        const isRecurringEdit =
          showRecurrence &&
          !isNew &&
          (Boolean(recurrenceRuleId) || workOrder?.task_source === "recurrence" || workOrder?.isVirtual);

        if (isRecurringEdit && !recurrenceRuleId && workOrder?.id) {
          try {
            const freshWO = await WorkOrderService.getWorkOrder(workOrder.id);
            const freshData = freshWO?.data || freshWO;
            if (freshData) {
              recurrenceRuleId = freshData.task_recurrence_rule_id || null;
              workOrderContext?.setWorkOrder?.(freshData);
            }
          } catch (e) {
            console.warn("Unable to refresh recurrence metadata before save:", e);
          }
        }

        if (isRecurringEdit) {
          if (!recurrenceRuleId) {
            UIkit.notification("Unable to identify recurrence rule for this task. Please close and reopen.", "danger");
            return;
          }

          let scope = recurringEditScope;
          if (scope) {
            setRecurringEditScope(null);
          } else {
            scope = await promptRecurringEditScope({
              itemLabel,
              summary: workOrder?.summary,
            });
          }
          if (!scope) {
            // Cancelled — leave the form open and untouched.
            return;
          }

          if (scope === "future" || scope === "all") {
            // Require a fully-loaded rule (rule_id + mode present, not a
            // loading/fallback placeholder). Submitting a half-loaded
            // recurrence would send mode=undefined and corrupt the split rule.
            const syncKey = String(recurrence?._syncKey || "");
            const recurrenceReady =
              recurrence?.rule_id &&
              recurrence?.mode &&
              !syncKey.startsWith("loading-") &&
              !syncKey.startsWith("fallback-");
            if (!recurrenceReady) {
              UIkit.notification(
                "Recurrence settings are still loading. Please wait and try again.",
                "warning"
              );
              return;
            }

            const existingRentalIds = Array.isArray(recurrence?.rental_ids)
              ? recurrence.rental_ids
              : [];
            const rentalIds = recurrence?.rental_scope === "all_rentals"
              ? []
              : data.rental_id
                ? [Number(data.rental_id)]
                : existingRentalIds;
            const futureStartDate = getOccurrenceStartDate(
              workOrder,
              existingSchedule,
              assignScheduleStartDate
            );
            if (scope === "future" && !futureStartDate) {
              UIkit.notification(
                "Unable to determine this occurrence's date. Please close and reopen the task.",
                "danger"
              );
              return;
            }

            const rulePayload = buildFutureRuleUpdatePayload({
              recurrence,
              data,
              assignScheduleStartTime,
              existingScheduleStartTime:
                existingSchedule?.scheduled_start_time || workOrder?.scheduled_start_time,
              assignScheduleDuration,
              selectedChecklistTemplate,
              checklistTemplateTouched,
              rentalIds,
              timePreference: "auto",
              editMode: scope === "all" ? "all" : "future_only",
            });

            try {
              if (scope === "all") {
                await TaskRecurrenceRuleService.update(recurrenceRuleId, rulePayload);
              } else {
                const splitRes = await TaskRecurrenceRuleService.split(recurrenceRuleId, {
                  effective_date: futureStartDate,
                  payload: rulePayload,
                });
                const newRuleId = splitRes?.data?.new_rule?.id;
                if (newRuleId) {
                  setRecurrence((prev) => ({
                    ...(prev || {}),
                    rule_id: newRuleId,
                    _syncKey: `rule-${newRuleId}`,
                  }));
                }
              }
            } catch (e) {
              console.error("Recurrence rule update failed:", e);
              UIkit.notification(
                scope === "all"
                  ? "Failed to update upcoming occurrences"
                  : "Failed to update future occurrences",
                "danger"
              );
              return;
            }

            closeWorkOrderAdd();
            UIkit.notification(
              scope === "all"
                ? "All upcoming occurrences updated"
                : "All future occurrences updated",
              "success"
            );
            workOrderContext.initWorkOrder();
            handleClearForm();
            await onSaved?.();
            return;
          }

          // scope === "this": materialize virtual occurrences first, then update.
        }

        let targetWorkOrderId = workOrder?.id || null;
        if (
          isRecurringEdit &&
          workOrder?.isVirtual &&
          recurrenceRuleId &&
          workOrder?.occurrence_key
        ) {
          try {
            const matRes = await TaskOccurrencesService.materialize({
              rule_id: recurrenceRuleId,
              occurrence_key: workOrder.occurrence_key,
            });
            const matWo = matRes?.data?.work_order;
            if (matWo?.id) {
              targetWorkOrderId = matWo.id;
              workOrderContext.setWorkOrder(matWo);
              // materialize creates the row WITH the rule's assignees. If the
              // form captured no assigners, carry the materialized row's
              // assignees into the update so the immediate updateWorkOrder
              // doesn't wipe them (its empty-assigners branch clears all).
              if (!data.assigners || !data.assigners.length) {
                const matAssignees = Array.isArray(matWo.work_order_assignees)
                  ? matWo.work_order_assignees
                  : [];
                if (matAssignees.length) {
                  data.assigners = matAssignees.map((a) => ({
                    type: a.assignee_type,
                    id: a.assignee_id,
                    assignment_role: a.assignment_role || "assignee",
                  }));
                }
              }
            }
          } catch (e) {
            console.error("Failed to materialize occurrence:", e);
            UIkit.notification("Failed to save this occurrence", "danger");
            return;
          }
        }

        const result = await (isNew
          ? insertWorkOrder(data)
          : updateWorkOrder(targetWorkOrderId, { ...data }));

        // After creating a new work order, save the schedule if one was configured
        if (isNew && result?.id && assignScheduleStartDate && assignScheduleStartTime) {
          const endTime = computeEndTimeStr(assignScheduleStartTime, assignScheduleDuration);
          try {
            await createWorkOrderSchedule(result.id, {
              scheduled_date: assignScheduleStartDate,
              scheduled_start_time: assignScheduleStartTime,
              scheduled_end_time: endTime || assignScheduleStartTime,
              scheduled_by: user?.id || user?.user_id || null,
              // Send assigners for backend overlap validation
              // (assignees are already saved with the WO, but we need the overlap check)
              assigners: data.assigners || [],
            });
            // Notify availability consumers so calendars/pickers reflect the new schedule
            workSchedulesCtx?.notifyScheduleChange?.(assignScheduleStartDate);
          } catch (schedErr) {
            console.log("Schedule creation after WO create failed:", schedErr);
            // Don't block the overall success — WO was created
          }
        }

        closeWorkOrderAdd();
        if (!result) {
          UIkit.notification(`${itemLabel} Add Failed`, "danger");
        } else {
          UIkit.notification(
            isNew === true
              ? `${itemLabel} Added Successfully`
              : `${itemLabel} Updated Successfully`,
            "success"
          );
          workOrderContext.initWorkOrder();
          handleClearForm();
          await onSaved?.();
        }
      } catch (err) {
        UIkit.notification("Failed", "error");
      }
    },
    [
      attachmentList,
      assignList,
      assignScheduleDuration,
      assignScheduleStartDate,
      assignScheduleStartTime,
      checklistTemplateTouched,
      createWorkOrderSchedule,
      existingSchedule,
      isNew,
      itemLabel,
      items,
      onSaved,
      recurrence,
      recurringEditScope,
      selectedChecklistTemplate,
      setError,
      setRecurringEditScope,
      showRecurrence,
      showTaskType,
      user,
      workOrder,
    ]
  );

  const handleError = (err) => {
    console.log(err);
  };

  const removeImage = (idx) => {
    let attachments = [...attachmentList];
    attachments.splice(idx, 1);
    setAttachments(attachments);
  };

  return (
    <>
      <div
        id="work-order-add"
        className="uk-modal-full hms-modals uk-modal uk-dark"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div className="hms-modals-main">
            <button
              className="uk-offcanvas-close uk-close-large"
              type="button"
              data-uk-close
            ></button>
            <div className="uk-modal-header">
              <h2 className="uk-modal-title">
                {isNew ? "Add a" : "Edit"} {itemLabel}
              </h2>
            </div>
            <div className="uk-modal-body uk-flex">
              <div className="wo-model-form-section">
                <form
                  data-uk-grid
                  onSubmit={handleSubmit(handleRegister, handleError)}
                >
                  <div className="wo-model-w-550 uk-grid" data-uk-grid>
                    <div className="uk-width-1-1">
                      <label className="uk-form-label">Summary*</label>
                      <div className="uk-form-controls">
                        <input
                          className={clsx("uk-input", {
                            "uk-form-danger": errors.summary,
                          })}
                          type="text"
                          placeholder=""
                          {...register("summary")}
                          // value="Coffee Order Title"
                        />
                      </div>
                    </div>

                    {showTaskType && (
                      <div className="uk-width-1-1">
                        <label className="uk-form-label">{taskTypeLabel}</label>
                        <div className="uk-form-controls">
                          <input type="hidden" {...register("task_type")} />
                          <div className={clsx("checklist-item-type-btn-group", { "uk-form-danger": errors.task_type })}>
                            {TASK_TYPE_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`checklist-item-type-btn ${selectedTaskType === opt.value ? "active" : ""}`}
                                onClick={() => handleTaskTypeSelect(opt.value)}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          {errors.task_type && (
                            <div className="uk-text-danger uk-margin-small-top">{errors.task_type.message}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {showRecurrence && (
                      recurrenceLoading ? (
                        <div className="uk-width-1-1 uk-text-meta">
                          Loading recurrence settings...
                        </div>
                      ) : recurrenceLoadFailed ? (
                        <div className="uk-width-1-1 uk-text-danger uk-text-small">
                          Unable to load recurrence settings for this task. Close and reopen before editing the series.
                        </div>
                      ) : (
                        <TaskRecurrenceFields
                          value={recurrence}
                          onChange={setRecurrence}
                          seriesFieldsEditable={seriesFieldsEditable}
                        />
                      )
                    )}

                    <div className="uk-width-1-1">
                      <label className="uk-form-label">Property ID</label>
                      <div className="uk-form-controls">
                        {!isRecurringEdit && (
                          <div
                            className={clsx("uk-search uk-search-default w-100", {
                              "uk-form-danger": errors.rental_id,
                            })}
                          >
                            <AutoComplete
                              style={{ width: "100%" }}
                              value={propertyKey}
                              options={propertyOptions}
                              filterOption={(inputValue, option) =>
                                option.label
                                  .toUpperCase()
                                  .indexOf(inputValue.toUpperCase()) !== -1
                              }
                              onChange={(value, option) => setPropertyKey(value)}
                              onSelect={(value, option) =>
                                handleSelectProperty(option)
                              }
                              placeholder="Begin typing the Property ID"
                            >
                              <Input.Search
                                size="medium"
                                onChange={debounceSearchRental}
                              ></Input.Search>
                            </AutoComplete>
                          </div>
                        )}
                        {errors.rental_id && (
                          <div className="uk-text-danger uk-margin-small-top">
                            {errors.rental_id.message}
                          </div>
                        )}
                      </div>
                    </div>
                    {property && (
                      <>
                        <div className="hms-search-result uk-width-1-1">
                          <div className="search-result-wrapper">
                            {!isRecurringEdit && (
                              <button
                                className="uk-button search-close-btn"
                                type="button"
                                onClick={() => {
                                  setProperty(null);
                                  setValue("rental_id", null);
                                }}
                              >
                                <span uk-icon="icon: close; ratio: 0.9"></span>
                              </button>
                            )}
                            <div className="uk-flex uk-flex-middle search-result-content">
                              <div className="search-result-key">Property ID</div>
                              <div className="search-result-value">
                                {property.pms_id}
                              </div>
                            </div>
                            <div className="uk-flex uk-flex-middle search-result-content">
                              <div className="search-result-key">Address</div>
                              <div className="search-result-value">
                                {property.address}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="uk-width-1-1 uk-margin-small-top">
                          <span
                            className="guest-detail-more-info"
                            uk-tooltip="Property Details"
                            onClick={(e) => {
                              e.preventDefault();
                              setPropertyDetailsOpen(true);
                            }}
                          >
                            Property Details <Image src="/images/more-info.svg" />
                          </span>
                        </div>
                      </>
                    )}

                    <div className="uk-width-1-1">
                      <label className="uk-form-label">Guest</label>
                      <div className="uk-form-controls">
                        <div className="uk-search uk-search-default w-100">
                          <AutoComplete
                            style={{ width: "100%" }}
                            value={guestKey}
                            options={guestOptions}
                            filterOption={false}
                            onChange={(value, option) => setGuestKey(value)}
                            onSelect={(value, option) =>
                              handleSelectGuest(option)
                            }
                            placeholder="Begin typing the Guest's Name"
                          >
                            <Input.Search
                              size="medium"
                              onChange={debounceSearchGuest}
                            ></Input.Search>
                          </AutoComplete>
                        </div>
                      </div>
                    </div>

                    {guest && (
                      <div className="hms-search-result uk-width-1-1">
                        <div className="search-result-wrapper">
                          <button
                            className="uk-button search-close-btn"
                            onClick={() => {
                              setGuest(null);
                              setValue("guest_reservation_id", null);
                            }}
                          >
                            <span uk-icon="icon: close; ratio: 0.9"></span>
                          </button>
                          <div className="uk-flex uk-flex-middle search-result-content">
                            <div className="search-result-key">Guest</div>
                            <div className="search-result-value">
                              {`${guest.first_name} ${guest.last_name}`}
                            </div>
                          </div>
                          <div className="uk-flex uk-flex-middle search-result-content">
                            <div className="search-result-key">Check-In & Out</div>
                            <div className="search-result-value">
                              {`${moment.utc(convertedGuest.check_in)
                                .local()
                                .format("MMM DD, YYYY")}`}&nbsp;-&nbsp; 
                              {`${moment.utc(convertedGuest.check_out)
                                .local()
                                .format("MMM DD, YYYY")}`}
                            </div>
                          </div>
                          <div className="uk-flex uk-flex-middle search-result-content">
                            <div className="search-result-key">Phone</div>
                            <div className="search-result-value">
                              {guest.formatted_phone}
                            </div>
                          </div>
                          <div className="uk-flex uk-flex-middle search-result-content">
                            <div className="search-result-key">Email</div>
                            <div className="search-result-value">
                              {guest.email}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="uk-width-1-1">
                      <div className="uk-form-label">Description</div>
                      <div className="uk-form-controls">
                        <textarea
                          className="uk-textarea"
                          rows="2"
                          {...register("description")}
                        ></textarea>
                      </div>
                    </div>

                    {/* <div className="uk-width-1-1">
                      <label className="uk-form-label">
                        Best time to enter Premise
                      </label>
                      <div className="uk-form-controls">
                        <div uk-form-custom="target: > * > span:first-child">
                          <select {...register("premise")}>
                            <option value="1:00 PM - 4:00 PM">
                              1:00 PM - 4:00 PM
                            </option>
                            <option value="1:00 PM - 5:00 PM">
                              1:00 PM - 5:00 PM
                            </option>
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span></span>
                            <span
                              uk-icon="icon: chevron-down"
                              className="hms-align-right"
                            ></span>
                          </button>
                        </div>
                      </div>
                    </div> */}
                    <div className="uk-width-1-2">
                      <label className="uk-form-label">Status</label>
                      <div className="uk-form-controls">
                        <div uk-form-custom="target: > * > span:first-child">
                          <select {...register("status")}>
                            <option value="new">New</option>
                            <option value="started">Started</option>
                            <option value="completed">Completed</option>
                            <option value="closed">Closed</option>
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span className="select-value">
                              {getValues("status")}
                            </span>
                            <span
                              uk-icon="icon: chevron-down"
                              className="hms-align-right"
                            ></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="uk-width-1-2" style={{ paddingLeft: '8px' }}>
                      <label className="uk-form-label">Priority</label>
                      <div className="uk-form-controls">
                        <div uk-form-custom="target: > * > span:first-child">
                          <select
                            defaultValue="medium"
                            {...register("priority")}
                          >
                            {PRIORITY_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span className="select-value">
                              {getPriorityLabel(getValues("priority"))}
                            </span>
                            <span
                              uk-icon="icon: chevron-down"
                              className="hms-align-right"
                            ></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="uk-width-1-2">
                      <label className="uk-form-label">Category</label>
                      <div className="uk-form-controls">
                        <div uk-form-custom="target: > * > span:first-child">
                          <select {...register("category")}>
                            <option value="">Select a Category</option>
                            {WORK_ORDER_CATEGORY_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span className="select-value">
                              {getValues("category")}
                            </span>
                            <span
                              uk-icon="icon: chevron-down"
                              className="hms-align-right"
                            ></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="uk-width-1-1">
                      <label className="uk-form-label">Manager</label>
                      <div className="uk-form-controls">
                        <Select
                          allowClear
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                          placeholder="Select a Manager"
                          value={watch("manager_id") || undefined}
                          onChange={(val) => setValue("manager_id", val || null)}
                          showArrow={false}
                          options={managerOptions}
                          getPopupContainer={() => document.body}
                        />
                      </div>
                    </div>

                    <div className="uk-width-1-1">
                      <label className="uk-form-label">Assign to</label>
                      <div className="uk-flex uk-flex-middle" style={{ gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Select
                            mode="multiple"
                            optionFilterProp="label"
                            allowClear
                            style={{ width: "100%" }}
                            placeholder="Select a Team Member"
                            value={assignList}
                            onChange={(selected) => setAssigns(selected)}
                            options={mergedAssignerOptions}
                            getPopupContainer={() => document.body}
                          />
                        </div>
                        <button
                          type="button"
                          className="uk-button uk-button-default"
                          style={{ flexShrink: 0, width: "auto", whiteSpace: "nowrap" }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openAssignPicker();
                          }}
                        >
                          <span uk-icon="users" />
                        </button>
                      </div>
                    </div>

                    {!isNew && existingSchedule?.scheduled_date && (
                      <div className="hms-search-result uk-width-1-1">
                        <div className="search-result-wrapper">
                          <div className="uk-flex uk-flex-middle search-result-content">
                            <div className="search-result-key">Scheduled For</div>
                            <div className="search-result-value">
                              {moment.utc(existingSchedule.scheduled_date).format("MMM DD, YYYY, ddd")}
                              {existingSchedule.scheduled_start_time && (
                                <>
                                  {" "}at{" "}
                                  {moment(
                                    existingSchedule.scheduled_start_time.slice(0, 5),
                                    "HH:mm"
                                  ).format("h:mma")}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="uk-width-1-1">
                      <div className="uk-form-label">Attachments</div>

                      <ImagePreview
                        attachments={attachmentList}
                        handler={removeImage}
                      />
                      <div className="js-upload uk-placeholder hms-file-uploader uk-text-center hms-file-delete">
                        <div className="uk-form-custom">
                          <div>
                            <Image
                              src="/images/upload-image.svg"
                              className="hms-navbar"
                            />
                          </div>
                          <input
                            key="thumnail_key"
                            type="file"
                            accept="image/*"
                            multiple
                            {...register("thumbnail")}
                            onChange={(evt) => {
                              setAttachments((prev) => {
                                return [...prev, ...evt.target.files];
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {user && (
                      <>
                        <div className="uk-width-1-1">
                          <label className="uk-form-label">Select Checklist</label>
                          <div className="uk-form-controls">
                            <Select
                              allowClear
                              style={{ width: "100%" }}
                              placeholder="None"
                              value={selectedChecklistTemplate || undefined}
                              onChange={(value) => onPickChecklistTemplate(value || "")}
                              onPopupScroll={handleChecklistScroll}
                              getPopupContainer={() => document.body}
                              loading={loadingChecklists}
                              notFoundContent={loadingChecklists ? <div style={{ textAlign: 'center', padding: '10px' }}>Loading...</div> : "No checklists found"}
                              options={[
                                { label: "None", value: "" },
                                ...checklistTemplates.map((t) => ({
                                  label: t.name,
                                  value: t.id,
                                })),
                              ]}
                            />
                          </div>
                        </div>

                        <hr />
                        <div className="uk-width-1-1">
                          <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small">
                            <label className="uk-form-label">Checklist Items</label>
                            <button
                              type="button"
                              className="uk-button hms-btn hms-gray-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openAddCheck();
                              }}
                            >
                              + Item
                            </button>
                          </div>

                          {items.length === 0 && (
                            <div className="uk-text-meta uk-text-center uk-padding-small">
                              {selectedChecklistTemplate 
                                ? "Template loaded. Items will appear if template has items."
                                : "No checklist items. Select a template or add items manually."}
                            </div>
                          )}

                          {items.length > 0 && (
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={handleDragEnd}
                            >
                              <SortableContext
                                items={items.map((i) => i._uiId || i.id)}
                                strategy={verticalListSortingStrategy}
                              >
                                <ul className="uk-list">
                                  {items.map((item, index) => (
                                    <SortableChecklistItem
                                      key={item._uiId || item.id}
                                      item={item}
                                      index={index}
                                      onChange={handleUpdateItem}
                                      onDelete={handleDeleteItem}
                                      onEdit={openEditCheckFromItem}
                                      itemTypes={ITEM_TYPES}
                                    />
                                  ))}
                                </ul>
                              </SortableContext>
                              <DragOverlay />
                            </DndContext>
                          )}
                        </div>
                      </>
                    )}

                    <hr />
                    <div className="uk-width-1-1">
                      <div className="hms-add-tag">
                        <div className="uk-form-label">Tags</div>
                        <div className="uk-flex uk-flex-top">
                          <Tags
                            value={tags || []}
                            onChange={(tags) => setValue("tags", tags)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="uk-width-1-1">
                      {/* <div className="attacment-display-area">
                        <Image src="/images/attachment.png" />
                      </div> */}
                    </div>
                    {/* <div className="uk-width-1-2">
                      <div className="attacment-display-area">
                        <Image src="/images/attachment.png" />
                      </div>
                    </div> */}
                    {/* <div className="uk-width-1-3">
                      <div className="attacment-display-area">
                        <Image src="/images/pdf-attachment.png" />
                        <div className="pdf-attachment-name">
                          <Image src="/images/pdf-icon.png" />
                          <span>pdf file name goes here..</span>
                        </div>
                      </div>
                    </div> */}
                  </div>
                  <div className="uk-flex uk-flex-between uk-flex-middle uk-width-1-1">
                    <div>
                      <button
                        className="uk-button hms-btn hms-red-btn"
                        type="submit"
                      >
                        {isProcessing ? (
                          <div data-uk-spinner="ratio: 0.5;"></div>
                        ) : (
                          "Save"
                        )}
                      </button>
                      <button
                        className="uk-button hms-btn uk-offcanvas-close"
                        type="button"
                        onClick={handleClearForm}
                      >
                        Cancel
                      </button>
                    </div>
                    {!isNew && showDelete && (workOrder?.id || workOrder?.isVirtual) && (
                      <div className="hms-action-btn">
                        <button
                          className="uk-button wo-edit-delete-btn"
                          type="button"
                          uk-toggle="target: #work-order-delete"
                          uk-tooltip="Delete"
                        >
                          <img src="/images/ops-delete.svg" alt="Delete" width={20} height={20} />
                        </button>
                      </div>
                    )}
                  </div>
                </form>
                {!workOrderContext.isNew && (
                  <>
                    {workOrderContext?.updated_at && (
                      <div className="s-msg-last-updated">
                        <ConvertUTCDateToLocalDate
                          date={workOrderContext.updated_at}
                          label="Last Updated: "
                        />
                      </div>
                    )}
                    {workOrderContext?.created_at && (
                      <div className="s-msg-last-updated">
                        <ConvertUTCDateToLocalDate
                          date={workOrderContext.created_at}
                          label="Added at: "
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <PropertyDetailsModal
        open={propertyDetailsOpen}
        rentalId={getValues("rental_id") || property?.rental_id}
        onClose={() => setPropertyDetailsOpen(false)}
      />
      <AssigneePickerModal
        open={assignPickerOpen}
        onClose={closeAssignPicker}
        onApply={applyAssignPicker}

        assignees={availabilityAssignees}
        getSchedule={getAssigneeSchedule}
        loadingSchedule={workSchedulesCtx.loading}
        loadingExistingSchedule={loadingExistingSchedule}
        hasExistingSchedule={!!existingSchedule}

        assignPickerDayOffset={assignPickerDayOffset}
        setAssignPickerDayOffset={setAssignPickerDayOffset}

        assignDraft={assignDraft}
        setAssignDraft={setAssignDraft}
        focusedAssignee={focusedAssignee}
        setFocusedAssignee={setFocusedAssignee}

        assignScheduleStartDate={assignScheduleStartDate}
        setAssignScheduleStartDate={setAssignScheduleStartDate}
        assignScheduleStartTime={assignScheduleStartTime}
        setAssignScheduleStartTime={setAssignScheduleStartTime}
        assignScheduleDuration={assignScheduleDuration}
        setAssignScheduleDuration={setAssignScheduleDuration}

        availability={availability}
        workOrderId={workOrder?.id || null}
        itemLabel={itemLabel}
      />
      {checkOpen && (
        <ChecklistItemModal
          open={checkOpen}
          mode={checkMode}
          initialValue={checkInitial}
          onClose={() => {
            setCheckOpen(false);
            setCheckInitial(null);
          }}
          onSave={(savedCheck) => {
            console.log("Saving item:", savedCheck);
            
            if (checkMode === "add") {
              setItems((prev) => [...prev, savedCheck]);
            } else {
              setItems((prev) =>
                prev.map((item) => (item._uiId === savedCheck._uiId ? savedCheck : item))
              );
            }
          }}
        />
      )}
    </>
  );
};

export default WorkOrderAdd;
